import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, Plus, Pencil, Trash2, Hammer, X, Check, AlertTriangle } from 'lucide-react';
import api from '../services/api';

/**
 * Making Style Master — admin-managed catalogue that drives wastage % +
 * making/gm + minimum making at estimate time.
 *
 * Each Making Style holds an array of per-purity rate rows (Phase F):
 *   rates: [{ purity, wastage_percentage, making_charges_per_gram,
 *             minimum_making_charges }, …]
 *
 * IMPORTANT: This is "Making Style", NOT the ERP-side "Design Style"
 * (which lives in `Item_Styles` and is shown on tag detail / estimate
 * for catalogue purposes only). The two are intentionally separate.
 */

// Matches backend `ALLOWED_PURITIES` (routes/making_style_master.py).
const PURITY_OPTIONS = [
  'Pure Gold', '22K', '18K', '14K', '9K',
  'Pure Silver', 'Silver 925', 'Platinum',
  'Other Metal',
];

const EMPTY_RATE = {
  purity: '',
  wastage_percentage: '',
  weight_threshold_grams: '5',          // sensible default
  small_piece_flat_charge: '',
  large_piece_per_gram_rate: '',
  minimum_making_charges: '',
};

export default function MakingStyleMaster() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/making-style-master');
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Making Styles');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    try {
      if (editing?.id) {
        await api.patch(`/making-style-master/${editing.id}`, payload);
        toast.success('Making Style updated');
      } else {
        await api.post('/making-style-master', payload);
        toast.success('Making Style added');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/making-style-master/${id}`);
      setConfirmId(null);
      toast.success('Making Style deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
      setConfirmId(null);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/making-style-master/${row.id}`, { active: !row.active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white px-4 py-4 sticky top-0 z-30 shadow">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="p-2 hover:bg-white/10 rounded-lg" data-testid="ms-back">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <Hammer className="w-6 h-6" />
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold leading-tight">Making Style Master</h1>
            <p className="text-xs text-indigo-100 mt-0.5">
              Wastage %, making/gm &amp; min making — per purity. Separate from ERP Design Style.
            </p>
          </div>
          <button onClick={() => setEditing({})}
            className="px-3 py-2 bg-white text-indigo-700 rounded-lg text-sm font-bold hover:bg-indigo-50 flex items-center gap-1.5"
            data-testid="ms-add">
            <Plus className="w-4 h-4" /> New
          </button>
        </div>
      </div>

      {/* List */}
      <div className="max-w-5xl mx-auto px-4 py-5">
        {loading ? (
          <div className="text-center text-slate-500 py-12 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
            <Hammer className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 text-sm">No Making Styles yet.</p>
            <p className="text-slate-400 text-xs mt-1">
              Add styles like &ldquo;Plain Chains &amp; Bangles&rdquo;, &ldquo;Diamond Designer Jewellery&rdquo;, etc.
            </p>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="ms-list">
            {items.map(row => <StyleCard
              key={row.id} row={row}
              onToggle={() => toggleActive(row)}
              onEdit={() => setEditing(row)}
              onDelete={() => setConfirmId(row.id)}
            />)}
          </div>
        )}
      </div>

      {/* Edit / Create dialog */}
      {editing !== null && (
        <MakingStyleDialog initial={editing}
          onClose={() => setEditing(null)} onSave={save} />
      )}

      {/* Delete confirm */}
      {confirmId && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full" data-testid="ms-delete-confirm">
            <h3 className="font-bold text-slate-900 mb-2">Delete this Making Style?</h3>
            <p className="text-sm text-slate-600 mb-4">
              This action cannot be undone. If any verified tag uses this style, deletion will be blocked.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmId(null)}
                className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-medium"
                data-testid="ms-delete-cancel">Cancel</button>
              <button onClick={() => remove(confirmId)}
                className="px-3 py-2 rounded-lg bg-rose-600 text-white text-sm font-bold hover:bg-rose-700"
                data-testid="ms-delete-confirm-btn">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── List card ────────────────────────────────────────────────────────────
function StyleCard({ row, onToggle, onEdit, onDelete }) {
  const rates = row.rates || [];
  const hasLegacyPurity = rates.some(r => r.purity == null);

  return (
    <div
      className={`bg-white rounded-2xl p-4 shadow-sm border ${row.active ? 'border-indigo-100' : 'border-slate-200 opacity-70'}`}
      data-testid={`ms-row-${row.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-slate-900 text-base">{row.name}</h3>
            {!row.active && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactive</span>
            )}
            {hasLegacyPurity && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-medium inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Legacy — edit to assign purity
              </span>
            )}
          </div>
          {/* Rate chips per purity */}
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {rates.length === 0 && (
              <span className="text-xs text-slate-500 italic">No rate rows yet.</span>
            )}
            {rates.map((r, i) => {
              const hasBracket = r.weight_threshold_grams && r.weight_threshold_grams > 0;
              return (
                <div
                  key={i}
                  className="text-[11px] bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 flex items-center gap-1.5"
                  data-testid={`ms-rate-${row.id}-${i}`}
                >
                  <span className="font-bold text-indigo-700">{r.purity || '?'}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-700">{r.wastage_percentage}%</span>
                  <span className="text-slate-400">·</span>
                  {hasBracket ? (
                    <>
                      <span className="text-slate-700">
                        ≤{r.weight_threshold_grams}g: ₹{(r.small_piece_flat_charge || 0).toLocaleString('en-IN')}
                      </span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-700">
                        &gt;{r.weight_threshold_grams}g: ₹{r.large_piece_per_gram_rate}/g
                      </span>
                    </>
                  ) : (
                    <span className="text-slate-700">₹{r.large_piece_per_gram_rate}/g</span>
                  )}
                  {(r.minimum_making_charges || 0) > 0 && (
                    <>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-700">min ₹{(r.minimum_making_charges || 0).toLocaleString('en-IN')}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <button onClick={onToggle}
            className={`px-2 py-1 rounded-md text-xs font-medium ${row.active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`ms-toggle-${row.id}`}>
            {row.active ? 'Active' : 'Off'}
          </button>
          <button onClick={onEdit}
            className="px-2 py-1 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 flex items-center gap-1 justify-center"
            data-testid={`ms-edit-${row.id}`}>
            <Pencil className="w-3 h-3" /> Edit
          </button>
          <button onClick={onDelete}
            className="px-2 py-1 rounded-md text-xs font-medium bg-rose-50 text-rose-700 hover:bg-rose-100 flex items-center gap-1 justify-center"
            data-testid={`ms-delete-${row.id}`}>
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Dialog ───────────────────────────────────────────────────────────────
function MakingStyleDialog({ initial, onClose, onSave }) {
  const isEdit = !!initial?.id;
  const [name, setName] = useState(initial?.name || '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [rates, setRates] = useState(() => {
    if (initial?.rates?.length) {
      return initial.rates.map(r => ({
        purity: r.purity || '',
        wastage_percentage: r.wastage_percentage ?? '',
        weight_threshold_grams: r.weight_threshold_grams ?? '5',
        small_piece_flat_charge: r.small_piece_flat_charge ?? '',
        large_piece_per_gram_rate: r.large_piece_per_gram_rate ?? '',
        minimum_making_charges: r.minimum_making_charges ?? '',
      }));
    }
    return [{ ...EMPTY_RATE }];
  });
  const [saving, setSaving] = useState(false);

  const usedPurities = new Set(rates.map(r => r.purity).filter(Boolean));

  const updateRate = (idx, field, value) => {
    setRates(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };
  const addRate = () => setRates(prev => [...prev, { ...EMPTY_RATE }]);
  const removeRate = (idx) => {
    if (rates.length <= 1) return;
    setRates(prev => prev.filter((_, i) => i !== idx));
  };

  // A row is valid when:
  //   • purity + wastage are present
  //   • IF threshold > 0 → BOTH small_flat & large_per_gram are present
  //   • IF threshold null/0 → only large_per_gram is required
  const rowValid = (r) => {
    if (!r.purity || r.wastage_percentage === '') return false;
    const th = parseFloat(r.weight_threshold_grams);
    if (th && th > 0) {
      return r.small_piece_flat_charge !== '' && r.large_piece_per_gram_rate !== '';
    }
    return r.large_piece_per_gram_rate !== '';
  };
  const canSave = !!name.trim() && rates.every(rowValid);

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    await onSave({
      name: name.trim(),
      active,
      rates: rates.map(r => {
        const th = parseFloat(r.weight_threshold_grams);
        const hasBracket = th && th > 0;
        return {
          purity: r.purity,
          wastage_percentage: parseFloat(r.wastage_percentage),
          weight_threshold_grams:    hasBracket ? th : null,
          small_piece_flat_charge:   hasBracket ? parseFloat(r.small_piece_flat_charge) : null,
          large_piece_per_gram_rate: parseFloat(r.large_piece_per_gram_rate),
          minimum_making_charges:    parseFloat(r.minimum_making_charges || 0),
        };
      }),
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl p-5 max-w-xl w-full max-h-[92vh] overflow-y-auto"
        data-testid="ms-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-900 text-lg">
            {isEdit ? 'Edit Making Style' : 'New Making Style'}
          </h3>
          <button onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-lg"
            data-testid="ms-dialog-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wide">Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder='e.g. "Diamond Designer Jewellery"'
              className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="ms-dialog-name" />
          </div>

          {/* Active */}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)}
              className="w-4 h-4 accent-indigo-600" data-testid="ms-dialog-active" />
            Active (shown in CSV picker)
          </label>

          {/* Purity rate rows */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                Purity Rates ({rates.length})
              </label>
              <button type="button" onClick={addRate}
                className="text-xs font-bold text-indigo-700 hover:text-indigo-900 flex items-center gap-1"
                data-testid="ms-dialog-add-rate">
                <Plus className="w-3.5 h-3.5" /> Add Purity
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mb-2">
              Add one row per purity this style supports. At estimate time the row matching the tag&apos;s purity is auto-selected.
            </p>
            <div className="space-y-2.5" data-testid="ms-dialog-rates">
              {rates.map((r, idx) => (
                <div key={idx}
                  className="border border-slate-200 rounded-lg p-2.5 bg-slate-50"
                  data-testid={`ms-dialog-rate-${idx}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <select
                      value={r.purity}
                      onChange={e => updateRate(idx, 'purity', e.target.value)}
                      className="flex-1 border border-slate-300 rounded-md px-2 py-1.5 text-sm font-bold bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      data-testid={`ms-dialog-rate-${idx}-purity`}
                    >
                      <option value="">— Purity —</option>
                      {PURITY_OPTIONS.map(p => (
                        <option key={p} value={p}
                          disabled={usedPurities.has(p) && r.purity !== p}>
                          {p}{usedPurities.has(p) && r.purity !== p ? ' (used)' : ''}
                        </option>
                      ))}
                    </select>
                    <button type="button" onClick={() => removeRate(idx)}
                      disabled={rates.length <= 1}
                      className="p-1.5 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`ms-dialog-rate-${idx}-remove`}
                    ><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>

                  {/* Wastage + Weight threshold */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="text-[10px] text-slate-500 uppercase">Wastage %</label>
                      <input type="number" step="0.01" min="0" max="100"
                        value={r.wastage_percentage}
                        onChange={e => updateRate(idx, 'wastage_percentage', e.target.value)}
                        placeholder="16"
                        className="w-full mt-0.5 border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        data-testid={`ms-dialog-rate-${idx}-wastage`} />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 uppercase">
                        Bracket threshold (g)
                        <span className="text-slate-400 normal-case ml-0.5">net</span>
                      </label>
                      <input type="number" step="0.01" min="0"
                        value={r.weight_threshold_grams}
                        onChange={e => updateRate(idx, 'weight_threshold_grams', e.target.value)}
                        placeholder="5"
                        className="w-full mt-0.5 border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        data-testid={`ms-dialog-rate-${idx}-threshold`} />
                    </div>
                  </div>

                  {/* Making charges — flat for ≤ threshold, per_gram for > threshold */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-500 uppercase">
                        Flat ₹ for ≤ {parseFloat(r.weight_threshold_grams) || 0}g
                      </label>
                      <input type="number" step="1" min="0"
                        value={r.small_piece_flat_charge}
                        onChange={e => updateRate(idx, 'small_piece_flat_charge', e.target.value)}
                        placeholder="8500"
                        disabled={!(parseFloat(r.weight_threshold_grams) > 0)}
                        className="w-full mt-0.5 border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:text-slate-400"
                        data-testid={`ms-dialog-rate-${idx}-flat`} />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 uppercase">
                        ₹/gm for &gt; {parseFloat(r.weight_threshold_grams) || 0}g
                      </label>
                      <input type="number" step="1" min="0"
                        value={r.large_piece_per_gram_rate}
                        onChange={e => updateRate(idx, 'large_piece_per_gram_rate', e.target.value)}
                        placeholder="900"
                        className="w-full mt-0.5 border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        data-testid={`ms-dialog-rate-${idx}-pergram`} />
                    </div>
                  </div>

                  <div className="mt-2">
                    <label className="text-[10px] text-slate-500 uppercase">Safety floor (Min Making ₹) — optional</label>
                    <input type="number" step="1" min="0"
                      value={r.minimum_making_charges}
                      onChange={e => updateRate(idx, 'minimum_making_charges', e.target.value)}
                      placeholder="0"
                      className="w-full mt-0.5 border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      data-testid={`ms-dialog-rate-${idx}-min`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose}
            className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-medium">
            Cancel
          </button>
          <button onClick={submit} disabled={!canSave || saving}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="ms-dialog-save">
            <Check className="w-4 h-4" /> {saving ? 'Saving…' : isEdit ? 'Save' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
